import React from "react";
import {
  Switch,
  Route,
  Redirect,
  useLocation,
} from "react-router-dom";
import styled from "styled-components";
import { MenuContextProvider } from "@ubnt/store.core/lib/contexts/MenuContext";
import { MicrositeProductPageContextProvider } from "@ubnt/store.core/lib/components/Microsites/context/MicrositeProductPageContext";
import {
  Language,
  SiteId,
} from "@ubnt/store.core/lib/contexts/MenuContext/mapping";

import { GlobalHeader } from "../Header";

import { Overlay } from "../Header/Overlay";

// Lazy load Footer (below the fold)
const Footer = React.lazy(() => import("layout/Footer").then(m => ({ default: m.Footer })));

// Lazy load all route components for better code splitting
const Wireless = React.lazy(() => import("pages/Routes/Wireless"));
const Wireless60ghz = React.lazy(() => import("pages/Routes/Wireless60ghz"));
const Wired = React.lazy(() => import("pages/Routes/Wired"));
const Fiber = React.lazy(() => import("pages/Routes/Fiber"));
const DesignCenter = React.lazy(() => import("pages/Routes/DesignCenter"));
const NotFound = React.lazy(() => import("pages/NotFound"));
const VideoModal = React.lazy(() => import("layout/VideoModal"));
import { useSiteSettings } from "../../context/SiteSettingsContext/context";
import { useBuyButton } from "hooks/useBuyButton";
import { useApp } from "../../context/AppContext";
import {
  MainContainer,
  SectionContainer,
  MainContainerContent,
} from "./layout";
import { REGIONAL_MICROSITE_PREFIX_PATH } from "../../constants/routes";
import "../../styles/fonts.scss";
import { getPageBasePaths } from "../../context/SiteSettingsContext/utils";
import { GLOBAL_SITE_CONFIG } from "context/SiteSettingsContext/constants";

const Wrapper = styled.div`
  flex: 1 1;
  display: flex;
  flex-direction: column;
  /* justify-content: center; */
  @media (max-width: 890px) {
    padding-top: 0;
  }
  @media screen and (-ms-high-contrast: active),
    screen and (-ms-high-contrast: none) {
    display: block;
    flex: auto;
  }
`;

const App: React.FC = function App() {
  const location = useLocation();
  const { store } = useSiteSettings();
  const { showBuyButton } = useBuyButton();
  const { responsiveMenuActive, video } = useApp();

  // #############################################################################
  // Render
  // #############################################################################
  return (
    <MenuContextProvider
      pathname={location.pathname}
      storeId={store.id}
      context={SiteId.Uisp}
      globalSiteConfig={GLOBAL_SITE_CONFIG}
      language={Language.En}
    >
      <MicrositeProductPageContextProvider showBuyButton={showBuyButton}>
        <MainContainer
          className={
            responsiveMenuActive ? "app-responsive-menu-active" : undefined
          }
        >
          <MainContainerContent>
            <GlobalHeader />
            <SectionContainer>
              <Overlay />
              <React.Suspense fallback={<div />}>
                <Switch>
                  <Redirect
                    from="/:url*(/+)"
                    to={location.pathname.slice(0, -1)}
                  />
                  {/* FIXME: Due to layout issue (only present in non-local env's) redirect from landing to uisp-overview that matches store uisp/ui category switcher */}
                  <Redirect exact from="/" to="/uisp-overview" />
                  <Redirect
                    exact
                    from={REGIONAL_MICROSITE_PREFIX_PATH}
                    to={`${REGIONAL_MICROSITE_PREFIX_PATH}/uisp-overview`}
                  />
                  <Route
                    path={getPageBasePaths("/wireless")}
                    render={() => (
                      <Wrapper>
                        <Wireless />
                      </Wrapper>
                    )}
                  />
                  <Route
                    path={getPageBasePaths("/wired")}
                    render={() => (
                      <Wrapper>
                        <Wired />
                      </Wrapper>
                    )}
                  />
                  <Route
                    path={getPageBasePaths("/uisp-overview")}
                    exact
                    render={() => (
                      <Wrapper>
                        <DesignCenter />
                      </Wrapper>
                    )}
                  />
                  <Route
                    path={getPageBasePaths("/60ghz-wireless")}
                    render={() => (
                      <Wrapper>
                        <Wireless60ghz />
                      </Wrapper>
                    )}
                  />
                  <Route
                    path={getPageBasePaths("/fiber")}
                    render={() => (
                      <Wrapper>
                        <Fiber />
                      </Wrapper>
                    )}
                  />

                  <Route
                    exact
                    path="/(.+)"
                    render={() => <Route exact render={() => <NotFound />} />}
                  />
                </Switch>
              </React.Suspense>
            </SectionContainer>
          </MainContainerContent>

          <React.Suspense fallback={<div style={{ minHeight: '400px' }} />}>
            <Footer />
          </React.Suspense>
          {video && (
            <React.Suspense fallback={null}>
              <VideoModal video={video} />
            </React.Suspense>
          )}
        </MainContainer>
      </MicrositeProductPageContextProvider>
    </MenuContextProvider>
  );
};

export default App;

